<template>
  <!-- Page Title -->
  <div class="page-title-section">
    <h1 class="page-title">Storico Ordini</h1>
    <p class="page-subtitle">Visualizza tutte le corse completate</p>
  </div>

  <!-- Headquarters Selector -->
  <div class="selector-card">
    <div class="selector-content">
      <el-icon class="selector-icon"><OfficeBuilding /></el-icon>
      <span class="selector-label">Seleziona una sede per visualizzare i booking passati</span>
    </div>
    <el-cascader
      v-model="input"
      :options="headquartersId"
      clearable
      placeholder="Tutte le sedi"
      @change="handleChange_headquarter"
      class="headquarter-selector"
    />
  </div>

  <div class="content-section">
    <FilterTableComponent
      :servizi="serviziBooking"
      @nomeChanged="handleNomeFilter"
      @targaChanged="handleTargaFilter"
      @servizioChanged="(newValue) => (servizioFilter = newValue)"
      @dataChanged="(newValue) => (dataFilter = newValue)"
      @resetClick="resetFilter"
      :nome="nomeFilter"
    ></FilterTableComponent>

    <div class="table-container" v-loading="isLoading" element-loading-text="Caricamento storico...">
      <el-table
        :data="homeTableData"
        stripe
        class="data-table"
      >
      <el-table-column prop="id" label="N° booking">
        <template #default="scope">
          <el-input :value="scope.row.id" />
        </template>
      </el-table-column>
      <el-table-column prop="sede" label="Sede" />
      <el-table-column prop="customer" label="Cliente" />
      <el-table-column prop="stato" label="Stato corsa">
        <template #default="scope">
          <el-tag
            v-if="scope.row.stato"
            :type="getStatusType(scope.row.stato)"
            effect="light"
          >
            {{ scope.row.stato }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="typeBooking" label="Servizio" />
      <el-table-column prop="auto" label="Auto" />
      <el-table-column prop="targa" label="Targa" />
      <el-table-column prop="ora" label="Data" />
      <el-table-column width="60">
        <template #default="scope">
          <router-link
            :to="{ path: `/booking_attivo/${scope.row.id}` }"
            class="routerLink"
          >
            <el-button size="small" type="primary">
              <el-icon>
                <DArrowRight />
              </el-icon>
            </el-button>
          </router-link>
        </template>
      </el-table-column>
      </el-table>
    </div>
  </div>

  <div class="pagination-container">
    <el-pagination
      background
      layout="prev, pager, next, total"
      @current-change="handleCurrentChange"
      :page-size="pageSize"
      :total="homeTable.length"
    >
    </el-pagination>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import bookingAPI from '../services/bookingAPI.js';
import { isLoggedStore } from '../store/logged';
import FilterTableComponent from '@/Components/FilterTableComponent.vue';
import { OfficeBuilding } from '@element-plus/icons-vue';
const dayjs = require('dayjs');
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

// Debounce utility
function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

export default {
  name: 'CorsePassatePage',
  components: {
    FilterTableComponent,
    OfficeBuilding,
  },
  data() {
    return {
      page: 1,
      pageSize: 10,
    };
  },
  setup() {
    let nomeFilter = ref('');
    let targaFilter = ref('');
    let servizioFilter = ref('');
    let dataFilter = ref('');
    let isLoading = ref(false);

    const storeLogin = isLoggedStore();
    let headquartersId = ref([]);
    const headquarters = ref([]);
    let tableData = ref([]);
    const input = ref('');

    // Debounced handlers
    const handleNomeFilter = debounce((newValue) => {
      nomeFilter.value = newValue;
    }, 300);

    const handleTargaFilter = debounce((newValue) => {
      targaFilter.value = newValue;
    }, 300);

    // Status type helper
    const getStatusType = (stato) => {
      if (stato === 'Concluso') return 'success';
      if (stato === 'Non ancora confermata') return 'danger';
      if (stato === 'Accettato dal cliente') return 'info';
      if (stato && stato.includes('Driver')) return 'warning';
      return '';
    };

    const typeBooking = (type, type_booking) => {
            if(type_booking=="booking"){
                switch (type) {
                case 1:
                    return "Pickup & Delivery"
                case 2:
                    return "Transfer"
                case 3:
                    return "Consegna un'auto"
                case 4:
                    return "Transfer e lavori"
                case 5:
                    return "Transfer con auto partner"
                case 6:
                    return "Transfer con auto partner e lavori"
                case 7:
                    return "Pickup & Delivery con auto partner"
            }
            }else if(type_booking=="restaurant"){
                return "Mobility"
            }
        }
        

    const loadHeadquarters = async () => {
      try {
        isLoading.value = true;
        const employeeId = storeLogin.employee.id;
        const response = await bookingAPI.getHeadquartersByEmployee(employeeId);

        response.data[0].headquarters.forEach((headquarter) => {
          let tableRow = {
            value: headquarter.id,
            label: `${headquarter.address} ${headquarter.street_number}, ${headquarter.city}`,
          };
          headquartersId.value.push(tableRow);
        });

        await loadAllBookings();
      } catch (e) {
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    };

    loadHeadquarters();

    const handleChange_headquarter = async (val) => {
      try {
        isLoading.value = true;
        if (val != null) {
          tableData.value = [];
          const today = dayjs(new Date())
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0)
            .format('YYYY-MM-DD HH:mm');

          const response = await bookingAPI.getBookingPassati(val, today);

          const details = await Promise.all(
            response.data.map((booking) => loadBooking(booking.id))
          );

          tableData.value = response.data.map((booking, i) => {
            const data = details[i];
            return {
              id: booking.id,
              sede:
                booking.headquarter.address +
                ' ' +
                booking.headquarter.street_number +
                ', ' +
                booking.headquarter.city,
              customer: booking.customer.name + ' ' + booking.customer.surname,
              stato: state(booking.state),
              auto:
                booking.car != null
                  ? booking.car.marca + ', ' + booking.car.modello
                  : '-',
              targa: booking.car != null ? booking.car.targa : '-',
              ora: data.date.current_date,
              new_date: data.new_date,
              typeBooking: typeBooking(data.typeOfBooking, booking.type_booking),
              headquarterId: booking.headquarter.id,
              type: data.typeOfBooking,
              dataBooking: booking.data,
            };
          });
        } else {
          await loadAllBookings();
        }
      } catch (e) {
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    };

    const loadAllBookings = async () => {
      try {
        isLoading.value = true;
        tableData.value = [];
        const today = dayjs(new Date())
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0)
          .format('YYYY-MM-DD HH:mm');

        const response = await bookingAPI.getBookingPassatiAll(today);
        // Il server torna gia' SOLO i booking del proprio partner:
        // il filtro client per sede era ridondante ed e' stato rimosso
        const filteredBookings = response.data;

        const details = await Promise.all(
          filteredBookings.map((booking) => loadBooking(booking.id))
        );

        tableData.value = filteredBookings.map((booking, i) => {
          const data = details[i];
          return {
            id: booking.id,
            sede:
              booking.headquarter.address +
              ' ' +
              booking.headquarter.street_number +
              ', ' +
              booking.headquarter.city,
            customer: booking.customer.name + ' ' + booking.customer.surname,
            tag: state(booking.state),
            auto:
              booking.car != null
                ? booking.car.marca + ', ' + booking.car.modello
                : '-',
            targa: booking.car != null ? booking.car.targa : '-',
            ora: data.date.current_date,
            newDate: data.date.newDate,
            tasks: [],
            type: data.typeOfBooking,
            isGone: data.isGone,
            isReturn: data.isReturn,
            dateGone: data.dateGone,
            dateReturn: data.dateReturn,
            typeBooking: typeBooking(data.typeOfBooking, booking.type_booking),
            headquarterId: booking.headquarter.id,
            stato: state(booking.state),
            dataBooking: booking.data,
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    };

    const loadBooking = async (id) => {
      try {
        let singleBooking = {
          dateGone: '',
          dateReturn: '',
          typeOfBooking: 0,
          isGone: false,
          isReturn: false,
          date: {
            current_date: '',
            newDate: '',
          },
        };

        const response = await bookingAPI.getBookingById(id);
        const booking = response.data;

        if (booking.pickup_bookings && booking.pickup_bookings.length > 0) {
          for (let i = 0; i < booking.pickup_bookings.length; i++) {
            const currentBooking = booking.pickup_bookings[i];
            booking.loaner == true
              ? (singleBooking.typeOfBooking = 7)
              : (singleBooking.typeOfBooking = 1);

            if (currentBooking.type_booking === 'gone') {
              singleBooking.dateGone = dayjs(currentBooking.date_partner)
                .utc()
                .format('DD/MM/YY HH:mm');
              singleBooking.isGone = true;
            } else {
              singleBooking.dateReturn = dayjs(currentBooking.date_partner)
                .utc()
                .format('DD/MM/YY HH:mm');
              singleBooking.isReturn = true;
            }
          }
        }
        if (booking.escort_bookings && booking.escort_bookings.length > 0) {
          for (let i = 0; i < booking.escort_bookings.length; i++) {
            const currentBooking = booking.escort_bookings[i];

            if (currentBooking.tasks == true && booking.loaner == true) {
              singleBooking.typeOfBooking = 6;
            } else if (
              currentBooking.tasks == false &&
              booking.loaner == true
            ) {
              singleBooking.typeOfBooking = 5;
            } else if (
              currentBooking.tasks == true &&
              booking.loaner == false
            ) {
              singleBooking.typeOfBooking = 4;
            } else {
              singleBooking.typeOfBooking = 2;
            }

            if (currentBooking.type_booking === 'gone') {
              singleBooking.dateGone = dayjs(currentBooking.date_partner)
                .utc()
                .format('DD/MM/YY HH:mm');
              singleBooking.isGone = true;
            } else {
              singleBooking.dateReturn = dayjs(currentBooking.date_partner)
                .utc()
                .format('DD/MM/YY HH:mm');
              singleBooking.isReturn = true;
            }
          }
        }
        if (booking.delivery_bookings && booking.delivery_bookings.length > 0) {
          for (let i = 0; i < booking.delivery_bookings.length; i++) {
            const currentBooking = booking.delivery_bookings[i];
            singleBooking.typeOfBooking = 3;

            if (currentBooking.type_booking === 'gone') {
              singleBooking.dateGone = dayjs(currentBooking.date_point)
                .utc()
                .format('DD/MM/YY HH:mm');
              singleBooking.isGone = true;
            } else {
              singleBooking.dateReturn = dayjs(currentBooking.date_point)
                .utc()
                .format('DD/MM/YY HH:mm');
              singleBooking.isReturn = true;
            }
          }
        }

        if (singleBooking.dateGone != '' && singleBooking.dateReturn != '') {
          singleBooking.date.current_date = `A: ${singleBooking.dateGone} R: ${singleBooking.dateReturn}`;
        } else {
          singleBooking.date.current_date =
            singleBooking.dateGone != ''
              ? `A: ${singleBooking.dateGone}`
              : `R: ${singleBooking.dateReturn}`;
        }

        return singleBooking;
      } catch (e) {
        console.error(e);
      }
    };
    
    const state = (state) => {
      switch (state) {
        case 'before':
          state = 'Non ancora confermata';
          break;
        case 'accepted':
          state = 'Accettato dal cliente';
          break;
        case 'startAPickup':
          state = 'Driver in viaggio';
          break;
        case 'driverScanJollyAPickup':
          state = 'Jolly e driver presso la sede';
          break;
        case 'customerAPickup':
          state = 'Driver arrivato dal cliente';
          break;
        case 'photoAPickup':
          state = 'Il driver ha fatto le foto';
          break;
        case 'scanAPickup':
          state = "Il driver ha ritirato l'auto";
          break;
        case 'partnerAPickup':
          state = 'Driver arrivato dal partner';
          break;

        case 'startRPickup':
          state = 'Driver in viaggio';
          break;
        case 'partnerRPickup':
          state = 'Driver arrivato dal partner';
          break;
        case 'customerRPickup':
          state = 'Driver arrivato dal cliente';
          break;
        case 'photoRPickup':
          state = 'Il driver ha fatto le foto';
          break;
        case 'scanRPickup':
          state = "Il driver ha consegnato l'auto";
          break;
        case 'driverScanJollyRPickup':
          state = 'Jolly presso il cliente';
          break;

        case 'startAPickupLoaner':
          state = 'Driver in arrivo';
          break;
        case 'partnerAPickupLoaner':
          state = 'Il Driver ha ritirato auto di cortesia';
          break;
        case 'customerAPickupLoaner':
          state = 'Driver arrivato dal cliente';
          break;
        case 'photoAPickupLoaner':
          state = 'Il driver ha fatto le foto';
          break;
        case 'scanAPickupLoaner':
          state = "Il cliente ha ritirato l'auto di cortesia";
          break;
        case 'partnersecondAPickupLoaner':
          state = 'Driver arrivato dal partner';
          break;

        case 'startRPickupLoaner':
          state = 'Driver in arrivo';
          break;
        case 'partnerRPickupLoaner':
          state = 'Il Driver ha ritirato auto di cortesia';
          break;
        case 'customerRPickupLoaner':
          state = 'Driver arrivato dal cliente';
          break;
        case 'photoRPickupLoaner':
          state = 'Il driver ha fatto le foto';
          break;
        case 'scanRPickupLoaner':
          state = "Il cliente ha ritirato l'auto di cortesia";
          break;

        case 'startADelivery':
          state = 'Driver in viaggio';
          break;
        case 'partnerADelivery':
          state = "Il driver ha ritirato l'auto";
          break;
        case 'photofirstADelivery':
          state = 'Il driver ha fatto le foto';
          break;
        case 'customerADelivery':
          state = 'Driver arrivato al punto di consegna';
          break;
        case 'photosecondADelivery':
          state = "Il driver ha consegnato l'auto";
          break;
        case 'driverScanJollyADelivery':
          state = 'Il jolly ha accompagnato il driver ';
          break;

        case 'startRDelivery':
          state = 'Driver in viaggio';
          break;
        case 'customerRDelivery':
          state = "Il driver ha ritirato l'auto";
          break;
        case 'photofirstRDelivery':
          state = 'Il driver ha fatto le foto';
          break;
        case 'partnerRDelivery':
          state = 'Driver arrivato al punto di consegna';
          break;
        case 'photosecondRDelivery':
          state = "Il driver ha consegnato l'auto";
          break;
        case 'driverScanJollyRDelivery':
          state = 'Il jolly ha accompagnato il driver ';
          break;

        case 'startAEscort':
          state = 'Driver in viaggio';
          break;
        case 'customerAEscort':
          state = 'Driver arrivato al punto di incontro con il cliente';
          break;
        case 'scanfirstAEscort':
          state = 'Il driver ha fatto salire il cliente in auto';
          break;
        case 'scanssecondAEscort':
          state = 'Il driver ha fatto portato il cliente al punto';
          break;

        case 'partnerAEscort':
          state = 'Il driver ha consegnato auto al partner';
          break;
        case 'startREscort':
          state = 'Driver in viaggio';
          break;
        case 'customerREscort':
          state = 'Driver arrivato al punto di incontro con il cliente';
          break;
        case 'scanfirstREscort':
          state = 'Il driver ha fatto salire il cliente in auto';
          break;
        case 'scansecondREscort':
          state = 'Il driver ha fatto portato il cliente al punto';
          break;

        case 'end':
          state = 'Concluso';
          break;
      }
      return state;
    };

    const serviziBooking = ref([
      {
        value: 'Pickup & Delivery',
        label: 'Pickup & Delivery',
      },
      {
        value: 'Transfer',
        label: 'Transfer',
      },
      {
        value: "Consegna un'auto",
        label: "Consegna un'auto",
      },
      {
        value: "Mobility",
        label: "Mobility",
      },
    ]);

    const homeTable = computed(function () {
      return tableData.value.filter((row) => {
        const headquarterMatch = row.headquarterId
          .toLowerCase()
          .includes(input.value.toLowerCase());
        const customerMatch = row.customer
          .toLowerCase()
          .includes(nomeFilter.value.toLowerCase());
        const targaMatch = row.targa
          .toLowerCase()
          .includes(targaFilter.value.toLowerCase());

          const servizioMatch = (() => {
          const servizio =
            servizioFilter.value[0] == undefined ? '' : servizioFilter.value[0];
          return row.typeBooking && typeof row.typeBooking === 'string'
            ? row.typeBooking.includes(servizio)
            : false;
        })();

        const dataMatch = dayjs(new Date(row.dataBooking))
          .format('YYYY-MM-DD')
          .includes(
            dataFilter.value === ''
              ? ''
              : dayjs(new Date(dataFilter.value)).format('YYYY-MM-DD')
          );

        return (
          headquarterMatch &&
          customerMatch &&
          targaMatch &&
          servizioMatch &&
          dataMatch
        );
      });
    });

    const resetFilter = async () => {
      window.location.reload();
    };

    return {
      tableData,
      homeTable,
      loadHeadquarters,
      headquartersId,
      headquarters,
      state,
      handleChange_headquarter,
      FilterTableComponent,
      serviziBooking,
      nomeFilter,
      targaFilter,
      servizioFilter,
      dataFilter,
      resetFilter,
      isLoading,
      handleNomeFilter,
      handleTargaFilter,
      getStatusType,
      input,
    };
  },
  computed: {
    homeTableData() {
      if (!this.homeTable || this.homeTable.length === 0) return [];
      return this.homeTable.slice(
        this.pageSize * this.page - this.pageSize,
        this.pageSize * this.page
      );
    },
  },
  watch: {
    homeTable() {
      this.page = 1;
    },
  },
  methods: {
    handleCurrentChange(val) {
      this.page = val;
    },
  },
};
</script>

<style scoped>
/* Page Title */
.page-title-section {
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #171717;
  margin: 0 0 8px 0;
  padding-bottom: 12px;
  border-bottom: 3px solid #4B30F2;
  display: inline-block;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #737373;
  margin: 0;
}

/* Selector Card */
.selector-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  border-left: 4px solid #4B30F2;
}

.selector-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selector-icon {
  font-size: 24px;
  color: #4B30F2;
}

.selector-label {
  font-size: 0.95rem;
  color: #525252;
  font-weight: 500;
}

.headquarter-selector {
  min-width: 280px;
}

/* Content Section */
.content-section {
  margin-top: 24px;
}

.table-container {
  overflow-x: auto;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 16px;
  min-height: 200px;
}

.data-table {
  min-width: 1000px;
  width: 100%;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

/* Responsive */
@media (max-width: 768px) {
  .selector-card {
    flex-direction: column;
    align-items: stretch;
  }

  .headquarter-selector {
    width: 100%;
    min-width: unset;
  }
}
</style>
